/**
 * What an unsupported step offers: why it cannot run, the alternatives that
 * can, and the two ways out of it.
 *
 * Shared by the create drawer's review list and the block panel, because they
 * show the same thing and had already started to drift.
 *
 * Swapping an alternative in keeps the unsupported step's id, so every exit
 * already pointing here points at the replacement rather than dangling.
 */

import { Button, Popconfirm } from 'antd';
import { violationMessage } from './utils/AOPViolationUtils';
import { ExclamationCircleFilled } from '@ant-design/icons';
import React, { FC } from 'react';
import {
	AOPStep,
	AOPUnsupportedReason,
	AOPUnsupportedStep,
	AOPViolation,
} from '../../types/AOPTypes';
import OWButton from '../ow_button/OWButton';
import { stepTitle } from './utils/AOPPlanUtils';

/**
 * What kind of thing Radar was asked for and has no term for. The violation
 * message says the rest — this is the one word that tells an author whether to
 * expect this request to become possible, and roughly when.
 */
const REASON_LABELS: { [reason: string]: string } = {
	channel: 'A way of contacting somebody that Radar cannot use',
	party: 'Somebody Radar cannot reach',
	operation: 'Something Radar cannot do to a work order',
	trigger: 'Something Radar cannot watch for',
	other: 'Something outside what Radar can express',
};

const reasonLabel = (reason: AOPUnsupportedReason): string =>
	REASON_LABELS[reason] ? REASON_LABELS[reason] : REASON_LABELS.other;

/** Opens the messenger this app already runs, with what Radar could not do. */
const contactSupport = (request: string) => {
	const intercom = (window as any).Intercom;

	if (intercom) {
		intercom('showNewMessage', `Radar cannot do this yet: ${request}`);
	}
};

interface AOPUnsupportedDetailProps {
	step: AOPUnsupportedStep;
	violations: AOPViolation[];
	isReadOnly?: boolean;
	onReplace: (alternative: AOPStep) => void;
	/** Left out when the step cannot be removed — a plan needs one step. */
	onRemove?: () => void;
}

export const AOPUnsupportedDetail: FC<AOPUnsupportedDetailProps> = ({
	step,
	violations,
	isReadOnly,
	onReplace,
	onRemove,
}) => (
	<div className="flex flex-col gap-xs">
		{/*
		  The reason first, then the service's own message. The reason says what
		  kind of thing this is; the message says what to do about it.
		*/}
		<span className="bodyregular text-character-secondary">{reasonLabel(step.reason)}</span>
		{violations.map((violation, index) => (
			<span key={index} className="bodyregular text-character-secondary">
				{violationMessage(violation)}
			</span>
		))}

		{step.alternatives.map((alternative, index) => (
			<div
				key={index}
				className="flex items-center gap-xs rounded border border-solid border-grey-4 bg-grey-1 px-s py-xs"
			>
				<span className="bodyregular min-w-0 flex-1 text-character-secondary">
					Suggested instead:{' '}
					<span className="bodyregular text-character-primary">{stepTitle(alternative)}</span>
				</span>
				{isReadOnly ? null : (
					<OWButton
						// Same size as every other button on a step, so the row does not
						// have one control a size larger than the rest.
						size="small"
						type="primary"
						// `.button` in typography.less uppercases button text globally;
						// the design has this in sentence case.
						className="aop-sentence-case"
						onClick={() => onReplace(alternative)}
					>
						Add this step
					</OWButton>
				)}
			</div>
		))}

		<div className="flex items-center gap-xs">
			<Button
				size="small"
				className="aop-sentence-case"
				onClick={() => contactSupport(step.request)}
			>
				Contact support
			</Button>
			{isReadOnly || !onRemove ? null : (
				<Popconfirm
					title="Remove this step? Anything pointing at it will need rewiring."
					okText="Remove"
					okButtonProps={{ danger: true }}
					cancelText="Cancel"
					onConfirm={onRemove}
				>
					<Button size="small" danger type="text" className="aop-sentence-case">
						Remove step
					</Button>
				</Popconfirm>
			)}
		</div>
	</div>
);

/** The red "Not executable" line, with its icon. */
export const AOPNotExecutable: FC = () => (
	<span className="bodyregular inline-flex items-center gap-2xs text-character-danger">
		<ExclamationCircleFilled />
		Not executable
	</span>
);
